import type { CalendarUpsertSchema } from '@repo/schemas/meeting/calendarUpsert';
import type { EventCalendarNavigation } from '@repo/types/calendar';
import type { MeetingPublic } from '@repo/types/meetings';

import { useEventCalendarRangeDates } from '@repo/hooks/useEventCalendarRangeDates';
import { AgendaDaysToShow, CalendarViewCellHeight, EventGap, EventHeight } from '@repo/lib/constants/calendar-constants';
import { VIEW_KEY } from '@repo/types/enums/viewKey';
import { addDays, addHours, addMonths, addWeeks, format, isValid } from 'date-fns';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

import { CalendarDndProvider } from '@/components/features/calendar/CalendarDndContext';
import EventMoreInfoModal from '@/components/features/calendar/EventMoreInfoModal';
import CancelMeeting from '@/components/features/calendar/form/CancelMeeting';
import CreateMeeting from '@/components/features/calendar/form/CreateMeeting';
import DeleteMeeting from '@/components/features/calendar/form/DeleteMeeting';
import EditMeeting from '@/components/features/calendar/form/EditMeeting';
import { UpdateDateTimeMeeting } from '@/components/features/calendar/form/UpdateDateTimeMeeting';
import { AgendaView, DayView, MonthView, WeekView } from '@/components/features/calendar/layout';
import EventCalendarHeader from '@/components/features/calendar/layout/EventCalendarHeader';
import { useCalendarScrollView } from '@/hooks/calendar';
import { useLocalStorage } from '@/hooks/local-storage';
import { parseCurrentDate, parseViewKey } from '@/utils/calendar';

const CALENDAR_VIEW_KEY_STORAGE_KEY = 'calendar-view-key';

const deserializeCalendarViewKey = (value: string): VIEW_KEY => {
  let parsed: unknown;

  try {
    parsed = JSON.parse(value);
  } catch {
    parsed = value;
  }

  return typeof parsed === 'string' ? parseViewKey(parsed) : VIEW_KEY.DAY;
};

export default function EventCalendar() {
  const [defaultValues, setDefaultValues] = useState<Partial<CalendarUpsertSchema> | undefined>(undefined);
  const [openEventInfo, setEventInfoOpen] = useState<boolean>(false);
  const [selectedEvent, setSelectedEvent] = useState<MeetingPublic | undefined>(undefined);
  const [openCreateEvent, setOpenCreateEvent] = useState<boolean>(false);
  const [openEditEvent, setOpenEditEvent] = useState<boolean>(false);
  const [openDeleteEvent, setOpenDeleteEvent] = useState<boolean>(false);
  const [openCancelEvent, setOpenCancelEvent] = useState<boolean>(false);
  const [openDateTimeUpdateEvent, setOpenDateTimeUpdateEvent] = useState<boolean>(false);
  const [storedViewKey, setStoredViewKey] = useLocalStorage<VIEW_KEY>(CALENDAR_VIEW_KEY_STORAGE_KEY, VIEW_KEY.DAY, { deserializer: deserializeCalendarViewKey });

  const [searchParams, setSearchParams] = useSearchParams({
    viewKey: storedViewKey,
    date: format(new Date(), 'dd-MM-yyyy'),
  });
  const currentDate = parseCurrentDate(searchParams.get('date'));
  const view = parseViewKey(searchParams.get('viewKey') ?? storedViewKey);

  const { ref: scrollContainerRef, scrollToCurrentTime } = useCalendarScrollView(view);

  const dateRange = useEventCalendarRangeDates(currentDate, view);

  const handleEventSelect = (event: MeetingPublic) => {
    setSelectedEvent(event);
    setEventInfoOpen(true);
  };

  const handleEventCreateOpen = () => {
    setDefaultValues(undefined);
    setSelectedEvent(undefined);
    setOpenCreateEvent(true);
  };

  const handleEventCreate = (date?: Date) => {
    if (!date) {
      return;
    }
    setDefaultValues({
      startDate: date?.toISOString(),
      endDate: addHours(date, 1).toISOString(),
    });

    setOpenCreateEvent(true);
    setSelectedEvent(undefined);
  };

  const handleEventUpdate = (event: MeetingPublic) => {
    setSelectedEvent(event);
    setOpenDateTimeUpdateEvent(true);
  };

  const handleEventEditOpen = () => {
    if (!selectedEvent) {
      return;
    }
    setOpenEditEvent(true);
    setEventInfoOpen(false);
  };

  const handleEventCancelOpen = () => {
    if (!selectedEvent) {
      return;
    }
    setOpenCancelEvent(true);
  };

  const handleEventDeleteOpen = () => {
    if (!selectedEvent) {
      return;
    }
    setOpenDeleteEvent(true);
  };

  const setCurrentDate = (number: number) => {
    if (!isValid(currentDate)) {
      return;
    }
    const sign = Math.sign(number) || 1;

    switch (view) {
      case VIEW_KEY.DAY:
        calendarNavigate({ newDate: addDays(currentDate, number) });
        break;
      case VIEW_KEY.WEEK:
        calendarNavigate({ newDate: addWeeks(currentDate, number) });
        break;
      case VIEW_KEY.MONTH:
        calendarNavigate({ newDate: addMonths(currentDate, number) });
        break;
      case VIEW_KEY.AGENDA:
        calendarNavigate({
          newDate: addDays(currentDate, sign * AgendaDaysToShow),
        });
        break;
    }
  };

  const calendarNavigate = ({ newViewKey, newDate }: EventCalendarNavigation) => {
    let dateString = searchParams.get('date') ?? '';
    let viewKey = parseViewKey(searchParams.get('viewKey') ?? storedViewKey);

    if (newDate && isValid(newDate)) {
      dateString = format(newDate, 'dd-MM-yyyy');
    }
    if (newViewKey) {
      viewKey = newViewKey;
      setStoredViewKey(newViewKey);
    }
    setSearchParams(`viewKey=${viewKey}&date=${dateString}`, {
      replace: true,
    });
  };

  // Add keyboard shortcuts for view switching
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if user is typing in an input, textarea or contentEditable element
      // or if the event dialog is open
      if (openEventInfo || e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || (e.target instanceof HTMLElement && e.target.isContentEditable)) {
        return;
      }

      switch (e.key.toLowerCase()) {
        case 'm':
          calendarNavigate({ newViewKey: VIEW_KEY.MONTH });
          break;
        case 'w':
          calendarNavigate({ newViewKey: VIEW_KEY.WEEK });
          break;
        case 'd':
          calendarNavigate({ newViewKey: VIEW_KEY.DAY });
          break;
        case 'a':
          calendarNavigate({ newViewKey: VIEW_KEY.AGENDA });
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [openEventInfo]);

  return (
    <div
      className="base-direction-col p-0 md:p-4"
      style={
        {
          '--event-height': `${EventHeight}px`,
          '--event-gap': `${EventGap}px`,
          '--week-cells-height': `${CalendarViewCellHeight}px`,
        } as React.CSSProperties
      }
    >
      <div ref={scrollContainerRef} className={'md:bg-background viewport-height flex w-full grow flex-col overflow-y-auto rounded-2xl md:border'}>
        <CalendarDndProvider onEventUpdate={handleEventUpdate}>
          <EventCalendarHeader changeDate={setCurrentDate} onTodayClick={scrollToCurrentTime} date={currentDate} onEventCreateOpen={handleEventCreateOpen} setNavigate={calendarNavigate} view={view} />
          <div className="flex flex-1 flex-col">
            {view === VIEW_KEY.MONTH && <MonthView rangeDates={dateRange} currentDate={currentDate} onEventSelect={handleEventSelect} onEventCreate={handleEventCreate} />}
            {view === VIEW_KEY.WEEK && <WeekView rangeDates={dateRange} currentDate={currentDate} onEventSelect={handleEventSelect} onEventCreate={handleEventCreate} />}
            {view === VIEW_KEY.DAY && <DayView rangeDates={dateRange} currentDate={currentDate} onEventSelect={handleEventSelect} onEventCreate={handleEventCreate} />}
            {view === VIEW_KEY.AGENDA && <AgendaView rangeDates={dateRange} currentDate={currentDate} onEventSelect={handleEventSelect} />}
          </div>
          <EventMoreInfoModal
            event={selectedEvent}
            open={openEventInfo}
            onOpenChange={setEventInfoOpen}
            onOpenEdit={handleEventEditOpen}
            onOpenDelete={handleEventDeleteOpen}
            onOpenCancel={handleEventCancelOpen}
          />
          <DeleteMeeting open={openDeleteEvent} onOpenChange={setOpenDeleteEvent} event={selectedEvent} queryParams={dateRange} />
          <EditMeeting id={selectedEvent?.id} open={openEditEvent} onOpenChange={setOpenEditEvent} queryParams={dateRange} />
          <CancelMeeting open={openCancelEvent} onOpenChange={setOpenCancelEvent} event={selectedEvent} queryParams={dateRange} />
          <CreateMeeting open={openCreateEvent} onOpenChange={setOpenCreateEvent} defaultValues={defaultValues} />
          <UpdateDateTimeMeeting event={selectedEvent} open={openDateTimeUpdateEvent} onOpenChange={setOpenDateTimeUpdateEvent} queryParams={dateRange} />
        </CalendarDndProvider>
      </div>
    </div>
  );
}
